import { Container, Sprite, Texture, type Application } from 'pixi.js';
import gsap from 'gsap';

interface PooledSprite extends Sprite {
  _inUse: boolean;
}

const POOL_SIZE = 128;

/**
 * SmokeEmitter — native Pixi v8 + GSAP particle system.
 * No @pixi/particle-emitter dependency; fully compatible with Pixi v8.
 *
 * Three burst types:
 *   trickle — tiny wisps while inhaling
 *   exhale  — billowing cloud on clean release
 *   cough   — chaotic wide-angle burst
 */
export class SmokeEmitter {
  readonly container: Container;
  private pool: PooledSprite[] = [];
  private texture!: Texture;

  constructor(_app: Application) {
    this.container = new Container();
    this.container.sortableChildren = false;
    this.container.zIndex = 50;
  }

  /** Must be called after assets are loaded (texture available in cache). */
  init(): void {
    this.texture = Texture.from('particle_smoke');
    for (let i = 0; i < POOL_SIZE; i++) {
      const s = new Sprite(this.texture) as PooledSprite;
      s.anchor.set(0.5);
      s.alpha = 0;
      s._inUse = false;
      this.pool.push(s);
      this.container.addChild(s);
    }
  }

  /** Small wisps at the ember tip while inhaling. */
  trickle(x: number, y: number): void {
    this.emit(x, y, 1, {
      angleMin: 255, angleMax: 285,
      speedMin: 30, speedMax: 70,
      lift: 25,
      startScale: 0.3, endScale: 1.2,
      startAlpha: 0.38,
      durationMin: 1.2, durationMax: 1.6,
      rotationMin: -40, rotationMax: 40,
    });
  }

  /** Big billowing exhale cloud. */
  exhale(x: number, y: number, directionSign: 1 | -1 = 1): void {
    this.emit(x, y, 20, {
      angleMin: 220, angleMax: 320,
      speedMin: 100, speedMax: 200,
      lift: 50,
      startScale: 0.7, endScale: 3.2,
      startAlpha: 0.78,
      durationMin: 2.0, durationMax: 3.0,
      rotationMin: -60, rotationMax: 60,
    });
    const ox = directionSign * 30;
    setTimeout(() => this.emit(x + ox, y - 12, 10, {
      angleMin: 230, angleMax: 310,
      speedMin: 80, speedMax: 160,
      lift: 45,
      startScale: 0.6, endScale: 2.8,
      startAlpha: 0.65,
      durationMin: 1.8, durationMax: 2.6,
      rotationMin: -50, rotationMax: 50,
    }), 120);
    setTimeout(() => this.emit(x + directionSign * 55, y - 22, 8, {
      angleMin: 240, angleMax: 300,
      speedMin: 60, speedMax: 130,
      lift: 40,
      startScale: 0.5, endScale: 2.4,
      startAlpha: 0.55,
      durationMin: 1.6, durationMax: 2.2,
      rotationMin: -40, rotationMax: 40,
    }), 260);
  }

  /** Generic burst. */
  burst(x: number, y: number, count = 8): void {
    this.emit(x, y, count, {
      angleMin: 240, angleMax: 300,
      speedMin: 40, speedMax: 90,
      lift: 30,
      startScale: 0.4, endScale: 1.6,
      startAlpha: 0.45,
      durationMin: 1.2, durationMax: 1.8,
      rotationMin: -50, rotationMax: 50,
    });
  }

  /** Heavy chaotic cough burst. */
  cough(x: number, y: number): void {
    this.emit(x, y, 28, {
      angleMin: 190, angleMax: 350,
      speedMin: 120, speedMax: 240,
      lift: 28,
      startScale: 0.8, endScale: 3.0,
      startAlpha: 0.68,
      durationMin: 1.6, durationMax: 2.4,
      rotationMin: -120, rotationMax: 120,
    });
  }

  destroy(): void {
    this.pool.forEach((s) => gsap.killTweensOf(s));
    this.container.destroy({ children: true });
    this.pool = [];
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private emit(
    x: number,
    y: number,
    count: number,
    cfg: {
      angleMin: number; angleMax: number;
      speedMin: number; speedMax: number;
      lift: number;
      startScale: number; endScale: number;
      startAlpha: number;
      durationMin: number; durationMax: number;
      rotationMin: number; rotationMax: number;
    }
  ): void {
    if (!this.texture) return;
    for (let i = 0; i < count; i++) {
      const s = this.acquire();
      if (!s) return;

      const angleDeg = cfg.angleMin + Math.random() * (cfg.angleMax - cfg.angleMin);
      const angleRad = (angleDeg * Math.PI) / 180;
      const speed = cfg.speedMin + Math.random() * (cfg.speedMax - cfg.speedMin);
      const dur = cfg.durationMin + Math.random() * (cfg.durationMax - cfg.durationMin);
      const rot = ((cfg.rotationMin + Math.random() * (cfg.rotationMax - cfg.rotationMin)) * Math.PI) / 180;

      const vx = Math.sin(angleRad) * speed;
      const vy = -Math.cos(angleRad) * speed;
      const endX = x + vx * dur;
      const endY = y + vy * dur - 0.5 * cfg.lift * dur * dur;

      s.position.set(x, y);
      s.scale.set(cfg.startScale);
      s.alpha = cfg.startAlpha;
      s.rotation = Math.random() * Math.PI * 2;

      gsap.to(s, {
        x: endX,
        y: endY,
        rotation: s.rotation + rot,
        alpha: 0,
        duration: dur,
        ease: 'power1.out',
        onComplete: () => this.release(s),
      });
      gsap.to(s.scale, {
        x: cfg.endScale,
        y: cfg.endScale,
        duration: dur,
        ease: 'power1.out',
      });
    }
  }

  private acquire(): PooledSprite | null {
    const s = this.pool.find((p) => !p._inUse);
    if (!s) return null;
    s._inUse = true;
    return s;
  }

  private release(s: PooledSprite): void {
    gsap.killTweensOf(s);
    gsap.killTweensOf(s.scale);
    s._inUse = false;
    s.alpha = 0;
  }
}
